"use client"

import { useEffect, useLayoutEffect, type ReactNode } from "react"
import { usePathname } from "next/navigation"

import { AppSidebar } from "@/components/app-sidebar"
import { useWorkspaces } from "@/components/tenant/hooks/use-workspaces"
import { SidebarProvider, useSidebar } from "@/components/ui/sidebar"
import { routeHasShell } from "@/lib/auth/route-shell"
import { SIDEBAR_COOKIE_NAME, SIDEBAR_PRELOAD_ATTRIBUTE } from "@/lib/shell/sidebar-state"
import { useWorkspaceStore } from "@/stores"

/**
 * The app's ONE sidebar, mounted once in the root layout (app/layout.tsx), above
 * every module.
 *
 * Until 2026-10-16 each of the twenty module layouts rendered its own
 * `<SidebarProvider><AppSidebar />`. Going from Phishing to Announcements
 * therefore unmounted the whole sidebar and built a new one: a new element, its
 * scroll position back at 0, the open group derived afresh from the route. That
 * is the flicker, and the "it goes anywhere" feeling the owner described
 * (measured on staging: the sidebar's DOM node was replaced across
 * /phishing -> /announcements/getting-started and kept within /phishing).
 *
 * The module layouts now draw only the content slot (`SidebarInset`) and their
 * own page chrome; this frame owns the provider and the sidebar for every route
 * that has a shell (lib/auth/route-shell.ts). It sits in the same place in the
 * tree for all of them, so React keeps the sidebar, its scroll offset and its
 * open group across a module change.
 *
 * Routes without a shell (login, the player, the portal, the super-admin
 * console, which has its own persistent sidebar) pass straight through.
 */
export function AppShellFrame({ children }: { children: ReactNode }) {
  const pathname = usePathname()
  if (!pathname || !routeHasShell(pathname)) return <>{children}</>
  return (
    <SidebarProvider>
      <RestoreSidebarState />
      <CloseMobileDrawerOnNavigation pathname={pathname} />
      <SyncWorkspaces />
      <AppSidebar />
      {children}
    </SidebarProvider>
  )
}

/**
 * Fills the workspace store from GET /workspaces/my on every page with the shell.
 *
 * Until 2026-10-23 only the workspace switcher (TeamSwitcher, inside AppSidebar) did,
 * and a phone mounts the sidebar's sheet only when the menu opens. A phone with no
 * workspace saved in this browser therefore chose none: every workspace-scoped read
 * stayed switched off (useWorkspaceScopedEnabled) and Data Health said "Failed to load
 * data" (staging, 23 Oct). Same rule as the switcher: a confirmed, non-empty list only.
 * The store picks the first workspace when none is chosen and keeps a valid choice. The
 * switcher keeps its own call; both read the one cached query.
 */
function SyncWorkspaces() {
  const { data, isSuccess } = useWorkspaces()
  const setWorkspaces = useWorkspaceStore((s) => s.setWorkspaces)
  useEffect(() => {
    if (isSuccess && data && data.length > 0) setWorkspaces(data)
  }, [data, isSuccess, setWorkspaces])
  return null
}

/**
 * Shuts the phone drawer whenever the route changes.
 *
 * Until the shell was shared, every move to another module rebuilt the provider and so
 * closed the drawer by accident; now one provider lives across modules, and without this a
 * phone user who taps a link into another module is left with the menu open over the new
 * page. (Within a module it never closed either.) `openMobile` is false on the desktop
 * layout, so this is a no-op there.
 */
function CloseMobileDrawerOnNavigation({ pathname }: { pathname: string }) {
  const { setOpenMobile } = useSidebar()
  useEffect(() => {
    setOpenMobile(false)
  }, [pathname, setOpenMobile])
  return null
}

/**
 * Seeds the collapsed/expanded choice from the cookie the provider writes.
 *
 * Only seven of the twenty module layouts used to read it (on the server, with
 * `cookies()`); the rest always opened expanded. The root layout must stay
 * static (reading cookies there would make every page dynamic), so the server
 * paints the sidebar expanded; on a hard load the head script
 * (lib/shell/sidebar-bootstrap.tsx) has already collapsed that element and marked
 * <html> before the first paint, and this moves React's own state to match, so the two
 * agree from hydration on (it renders the very attributes the script wrote). On a
 * client-side arrival into the shell (no server paint to fix) it is what applies the
 * choice, before the first paint of the new screen. It runs once per visit to the shell,
 * not once per module.
 */
function RestoreSidebarState() {
  const { setOpen } = useSidebar()
  useLayoutEffect(() => {
    const saved = document.cookie
      .split("; ")
      .find((part) => part.startsWith(`${SIDEBAR_COOKIE_NAME}=`))
      ?.slice(SIDEBAR_COOKIE_NAME.length + 1)
    if (saved === "false") setOpen(false)
    // React owns the state now: drop the head script's hand-off marker so it can
    // never go stale (nothing reads it; it is there to be seen, and tested).
    document.documentElement.removeAttribute(SIDEBAR_PRELOAD_ATTRIBUTE)
    // Once, on mount: `setOpen` changes identity with the state it sets.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  return null
}
